import { TOPICS } from "@/data/topics";
import TopicSection from "@/components/site/TopicSection";
import Quiz from "@/components/site/Quiz";

function Bullets({ items, testid }) {
  return (
    <ul className="grid gap-3 sm:grid-cols-2" data-testid={testid}>
      {items.map((b, i) => (
        <li
          key={i}
          className="flex items-start gap-3 rounded-2xl border-[3px] border-tiger-deep bg-tiger-cream px-4 py-3 shadow-sticker"
        >
          <span className="mt-1 inline-block h-3 w-3 flex-shrink-0 rounded-full bg-tiger-orange" />
          <span className="font-body text-base text-tiger-deep sm:text-lg">
            {b}
          </span>
        </li>
      ))}
    </ul>
  );
}

function BodyParts({ parts }) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {parts.map((p) => (
        <div
          key={p.name}
          className="rounded-2xl border-[3px] border-tiger-deep bg-white px-4 py-3 shadow-sticker"
        >
          <p className="font-heading text-lg font-bold text-tiger-orange">
            {p.name}
          </p>
          <p className="mt-1 font-body text-sm text-tiger-deep sm:text-base">
            {p.text}
          </p>
        </div>
      ))}
    </div>
  );
}

function TigerTypes({ types }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {types.map((t) => (
        <div
          key={t.name}
          className="flex items-center gap-3 rounded-2xl border-[3px] border-tiger-deep bg-jungle-light px-4 py-3 shadow-sticker"
        >
          <span className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-xl border-[3px] border-tiger-deep bg-white text-2xl shadow-sticker">
            {t.icon}
          </span>
          <div>
            <p className="font-heading text-lg font-bold text-jungle-dark">
              {t.name}
            </p>
            <p className="font-body text-sm text-tiger-deep">{t.where}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function LifeStages({ stages }) {
  return (
    <div className="space-y-3">
      {stages.map((s, i) => (
        <div
          key={s.age}
          className="flex items-center gap-4 rounded-2xl border-[3px] border-tiger-deep bg-white px-4 py-3 shadow-sticker"
        >
          <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border-[3px] border-tiger-deep bg-tiger-orange font-heading font-bold text-white">
            {i + 1}
          </span>
          <div>
            <p className="font-heading text-base font-bold text-tiger-deep">
              {s.age}
            </p>
            <p className="font-body text-base text-tiger-deep">{s.text}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function CompareTable({ rows }) {
  return (
    <div className="overflow-hidden rounded-3xl border-[4px] border-tiger-deep bg-white shadow-stickerLg">
      <div className="grid grid-cols-3 bg-tiger-deep text-white">
        <div className="px-4 py-3 font-heading text-base font-bold">
          Question
        </div>
        <div className="px-4 py-3 font-heading text-base font-bold border-l-[3px] border-white/30">
          🐯 Tiger
        </div>
        <div className="px-4 py-3 font-heading text-base font-bold border-l-[3px] border-white/30">
          🦁 Lion
        </div>
      </div>
      {rows.map((r, i) => (
        <div
          key={r.trait}
          className={`grid grid-cols-3 ${
            i % 2 === 0 ? "bg-tiger-cream" : "bg-white"
          } border-t-[3px] border-tiger-deep`}
        >
          <div className="px-4 py-3 font-body font-bold text-tiger-deep">
            {r.trait}
          </div>
          <div className="px-4 py-3 font-body text-tiger-deep border-l-[3px] border-tiger-deep">
            {r.tiger}
          </div>
          <div className="px-4 py-3 font-body text-tiger-deep border-l-[3px] border-tiger-deep">
            {r.lion}
          </div>
        </div>
      ))}
    </div>
  );
}

function Reasons({ reasons }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {reasons.map((r) => (
        <div
          key={r.title}
          className="rounded-2xl border-[3px] border-tiger-deep bg-white px-4 py-3 shadow-sticker"
        >
          <p className="font-heading text-lg font-bold text-tiger-orange">
            {r.title}
          </p>
          <p className="mt-1 font-body text-base text-tiger-deep">{r.text}</p>
        </div>
      ))}
    </div>
  );
}

function HelpActions({ actions }) {
  const colors = ["bg-sun", "bg-jungle-leaf", "bg-tiger-orange/30"];
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {actions.map((a, i) => (
        <div
          key={a.title}
          className={`rounded-2xl border-[3px] border-tiger-deep px-4 py-3 shadow-sticker ${
            colors[i % colors.length]
          }`}
        >
          <p className="font-heading text-lg font-bold text-tiger-deep">
            {a.title}
          </p>
          <p className="mt-1 font-body text-base text-tiger-deep">{a.text}</p>
        </div>
      ))}
    </div>
  );
}

function FactList({ facts }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {facts.map((f, i) => (
        <div
          key={i}
          className="rounded-2xl border-[3px] border-tiger-deep bg-white px-4 py-3 shadow-sticker"
        >
          <p className="font-body text-base text-tiger-deep sm:text-lg">
            <span className="font-heading font-bold text-tiger-orange">
              #{i + 1}{" "}
            </span>
            {f}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function Topics() {
  return (
    <div className="paper">
      {TOPICS.map((topic, idx) => (
        <TopicSection key={topic.id} topic={topic} index={idx}>
          {topic.bullets && (
            <Bullets items={topic.bullets} testid={`bullets-${topic.id}`} />
          )}
          {topic.parts && <BodyParts parts={topic.parts} />}
          {topic.types && <TigerTypes types={topic.types} />}
          {topic.stages && <LifeStages stages={topic.stages} />}
          {topic.compare && <CompareTable rows={topic.compare} />}
          {topic.reasons && <Reasons reasons={topic.reasons} />}
          {topic.actions && <HelpActions actions={topic.actions} />}
          {topic.facts && (
            <>
              <FactList facts={topic.facts} />
              <Quiz />
            </>
          )}
        </TopicSection>
      ))}
    </div>
  );
}
